// Scripts for the homepage demos, keyed by data-demo. Each gets the demo
// (`d`) and runs one loop; the demo resets the screen between loops. Markup
// lives in app/views/static/demos/.

// Shows the demo's toast for a while. Often fired without await, so it
// swallows the cancellation when the demo stops mid-toast.
const toast = async (d, text, ms = 1900) => {
  const el = d.find('[data-toast]')
  if (text) el.querySelector('[data-toast-text]').textContent = text
  el.dataset.on = ''
  try {
    await d.wait(ms)
    delete el.dataset.on
  } catch {}
}

// Starts the player around [data-t=name]; the CSS progress bar does the rest.
const play = (d, name) => {
  d.find(`[data-t="${name}"]`).closest('[data-player]').dataset.playing = ''
}

const bump = (d, key, by) => {
  const el = d.find(`[data-count="${key}"]`)
  el.textContent = Number(el.textContent) + by
}

// Owner panel buttons need two clicks: the first asks "Confirm …?".
const confirmClick = async (d, name) => {
  await d.clickOn(name)
  d.find(`[data-t="${name}"]`).dataset.confirm = ''
  await d.wait(650)
  await d.click()
}

export default {
  // Play → shortlist → dismiss → assign to role.
  async submissions(d) {
    await d.wait(900)
    await d.clickOn('play-clark')
    play(d, 'play-clark')
    const plays = d.find('[data-row="clark"] [data-plays]')
    plays.textContent = Number(plays.textContent) + 1
    await d.wait(1800)
    await confirmClick(d, 'shortlist-clark')
    d.find('[data-row="clark"]').dataset.gone = ''
    bump(d, 'unsorted', -1)
    bump(d, 'shortlisted', 1)
    toast(d, 'Marked as shortlisted.')
    await d.wait(1200)
    await confirmClick(d, 'dismiss-bruce')
    d.find('[data-row="bruce"]').dataset.gone = ''
    bump(d, 'unsorted', -1)
    bump(d, 'dismissed', 1)
    toast(d, 'Status changed.')
    await d.wait(1200)
    await d.clickOn('assign-diana')
    d.set('modal', 'on')
    await d.wait(1300)
    await d.clickOn('do-it')
    d.unset('modal')
    d.find('[data-row="diana"]').dataset.gone = ''
    bump(d, 'unsorted', -1)
    bump(d, 'assigned', 1)
    await toast(d, 'Status changed.', 2400)
    await d.wait(600)
  },

  // Voice Actor → "British" → "Gravelly" → play a result.
  async talent(d) {
    await d.wait(900)
    await d.clickOn('kind-voice')
    d.set('kind', 'on')
    await d.wait(500)
    await d.clickOn('accents')
    await d.type('[data-field="accents"]', 'British', { ms: 70 })
    d.find('[data-field="accents"]').textContent = ''
    d.set('british', 'on')
    await d.wait(700)
    await d.clickOn('voice')
    await d.type('[data-field="voice"]', 'Gravelly', { ms: 70 })
    d.find('[data-field="voice"]').textContent = ''
    d.set('gravelly', 'on')
    await d.wait(900)
    await d.scrollTo('results', 40)
    await d.clickOn('play-alfred')
    play(d, 'play-alfred')
    await d.wait(3400)
  },

  // New Credited Member → "pet" → pick Peter → what he did → Save.
  async credits(d) {
    await d.wait(900)
    await d.clickOn('new-member')
    d.set('adding', 'on')
    await d.wait(500)
    await d.clickOn('username')
    await d.type('[data-field="who"]', 'pet', { ms: 110 })
    d.set('suggest', 'on')
    await d.wait(600)
    await d.clickOn('suggest-peter')
    d.unset('suggest')
    d.set('picked', 'on')
    await d.wait(400)
    await d.clickOn('credited')
    await d.type('[data-field="credited"]', 'Audio Engineer')
    await d.clickOn('role')
    await d.type('[data-field="role"]', 'Self', { ms: 90 })
    await d.wait(300)
    await d.clickOn('save')
    d.set('saved', 'on')
    await toast(d, "Credit sent. Peter's about to have a very good day.", 2600)
    await d.wait(800)
  },

  // Paid → Videogame → British → dog → open the corgi.
  async projects(d) {
    await d.wait(900)
    await d.clickOn('pay-paid')
    d.set('paid', 'on')
    await d.wait(600)
    await d.clickOn('filters')
    d.set('panel', 'on')
    await d.wait(500)
    await d.clickOn('cat-videogame')
    d.set('videogame', 'on')
    await d.wait(500)
    await d.clickOn('add-accents')
    d.set('pop', 'accents')
    await d.type('[data-field="accents"]', 'British', { ms: 70 })
    d.unset('pop')
    d.set('british', 'on')
    await d.wait(500)
    await d.clickOn('add-voice')
    d.set('pop', 'voice')
    await d.type('[data-field="voice"]', 'dog', { ms: 110 })
    d.unset('pop')
    d.set('digging', 'on')
    await d.wait(900)
    d.unset('digging')
    d.set('dog', 'on')
    d.find('[data-count-line]').innerHTML = '<b class="text-gray-900">1</b> open role waiting on you. Just the one. It\'s perfect.'
    await d.wait(700)
    await d.scrollTo('role-pawsome', 160)
    await d.clickOn('role-pawsome')
    d.set('open', 'on')
    await d.wait(3200)
  },

  // Apply → drop a file → upload → confetti → note → Save.
  async audition(d) {
    await d.wait(1000)
    await d.clickOn('apply')
    d.set('view', 'upload')
    d.url('castingcall.club/submissions/new')
    await d.wait(900)
    await d.clickOn('drop')
    d.set('file', 'on')
    await d.wait(100)
    d.set('uploaded', 'on')
    await d.wait(2000)
    d.set('view', 'done')
    d.url('castingcall.club/submissions/48213/edit?success=true')
    await d.wait(1600)
    await d.clickOn('note')
    await d.type('[data-field="note"]', "Three takes. Take 3 is the poshest. I also have a pet iguana, if that helps.", { ms: 30 })
    await d.wait(300)
    await d.clickOn('save')
    await toast(d, null, 2800)
    await d.wait(600)
  },

  // Play a demo → Follow → skills → recommendations → credits.
  async profile(d) {
    await d.wait(1000)
    await d.clickOn('demo-0', { dx: -80 })
    play(d, 'demo-0')
    await d.wait(1600)
    await d.clickOn('follow')
    d.set('following', 'on')
    d.find('[data-followers]').textContent = '2,482'
    toast(d, null, 2200)
    await d.wait(1800)
    await d.scrollTo('skills')
    await d.wait(1300)
    await d.scrollTo('recs')
    await d.wait(2200)
    await d.scrollTo('credits')
    await d.wait(2400)
  },
}
